/**
 * InspectionFeeSection — a bevizsgálási díj a munkalapon (kör-69 `2b`, FK-SZE-3).
 *
 * ⛔ `0` FE-számítás. A díjat a `#1875` óta a szerver számítja
 * (`hourlyRateNet / 2 * (1 + ÁFA) * sürgősségi szorzó`); ez a komponens a
 * `GET /worksheets/:id/inspection-fee` borítékát rendereli, semmi mást.
 *
 * A HIÁNY-ág látható: ha nincs érvényes kategória-óradíj, a BE `fee: null` +
 * `inspectionFeeMissing: true`-t ad, és itt jelzés jelenik meg — SOHA nem néma
 * `0 Ft` és nem default-összeg. (A kivezetett `InspectionStep.INSPECTION_FEE_TABLE`
 * pontosan ezt csinálta: ismeretlen kategóriára csendben `5000`-et mutatott.)
 */

import { getInspectionFee, overrideInspectionFee } from '@/api/worksheets';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/lib/formatters';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

interface InspectionFeeSectionProps {
  worksheetId: string;
}

interface FeeState {
  fee: number | null;
  inspectionFeeMissing: boolean;
  hourlyRateNet: number | null;
  vatRatePercent: number;
  isUrgent: boolean;
  isDisassembled: boolean;
  description: string;
}

export function InspectionFeeSection({ worksheetId }: InspectionFeeSectionProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<FeeState | null>(null);
  const [isOverriding, setIsOverriding] = useState(false);
  const [overrideValue, setOverrideValue] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await getInspectionFee(worksheetId));
    } catch {
      // wire-gate vagy hálózati hiba: inkább semmit, mint validálatlan money-payloadot
      setData(null);
    }
  }, [worksheetId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!data) return null;

  const handleOverrideSubmit = async () => {
    const amount = Number(overrideValue);
    // Az indok a BE-n is kötelező (FR70-minta) — a néma 400 helyett itt fogjuk meg.
    if (!Number.isFinite(amount) || amount <= 0 || overrideReason.trim().length === 0) return;
    await overrideInspectionFee(worksheetId, amount, overrideReason.trim());
    setIsOverriding(false);
    await load();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('worksheet.inspectionFee.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.inspectionFeeMissing ? (
          <div
            data-testid="inspection-fee-missing"
            className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20"
          >
            <p className="font-medium text-amber-800 dark:text-amber-300">
              {t('worksheet.inspectionFee.missing')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{data.description}</p>
          </div>
        ) : (
          <>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('worksheet.inspectionFee.suggested')}
              </p>
              <p data-testid="inspection-fee-amount" className="text-lg font-semibold">
                {formatCurrency(data.fee ?? 0)}
              </p>
            </div>

            {data.hourlyRateNet !== null && (
              <p data-testid="inspection-fee-base" className="text-sm text-muted-foreground">
                {t('worksheet.inspectionFee.hourlyRateBase', {
                  rate: formatCurrency(data.hourlyRateNet),
                  vat: String(data.vatRatePercent),
                })}
              </p>
            )}

            {data.isUrgent && (
              <p
                data-testid="inspection-fee-urgent"
                className="text-sm font-medium text-orange-700 dark:text-orange-400"
              >
                {t('worksheet.inspectionFee.urgent')}
              </p>
            )}

            {!isOverriding ? (
              <button
                type="button"
                data-testid="inspection-fee-override-start"
                onClick={() => setIsOverriding(true)}
                className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                {t('worksheet.inspectionFee.override')}
              </button>
            ) : (
              <div className="space-y-2">
                <input
                  type="number"
                  data-testid="inspection-fee-override-input"
                  value={overrideValue}
                  onChange={e => setOverrideValue(e.target.value)}
                  aria-label={t('worksheet.inspectionFee.override')}
                  className="w-full rounded-md border px-3 py-2"
                />
                <input
                  type="text"
                  data-testid="inspection-fee-override-reason"
                  value={overrideReason}
                  onChange={e => setOverrideReason(e.target.value)}
                  aria-label={t('worksheet.inspectionFee.overrideReason')}
                  placeholder={t('worksheet.inspectionFee.overrideReason')}
                  className="w-full rounded-md border px-3 py-2"
                />
                <button
                  type="button"
                  data-testid="inspection-fee-override-submit"
                  onClick={() => void handleOverrideSubmit()}
                  className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white"
                >
                  {t('worksheet.inspectionFee.confirm')}
                </button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
